import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import FormInputText from '../../components/form/FormInputText';
import { emailField, entityNameField, identifierField } from '../../utils/validation-helper';
import { EnabledProducts, LockDeskHours, Tenant } from '../../models/tenant';
import TenantService from '../../services/tenantService';
import { Toast } from 'primereact/toast';
import { TimezoneOptions } from '../../utils/constants';
import FormDropdown from '../../components/form/FormDropdown';
import FormCheckbox from '../../components/form/FormCheckbox';
import { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { classNames } from 'primereact/utils';
import { Editor } from '@monaco-editor/react';

interface EditTenantProps {
  onCancel: () => void;
  onSave?: (_tenant: Tenant) => void;
  tenant?: Tenant | null;
}

// Normalize time string from server to remove seconds (HH:MM:SS → HH:MM)
const normalizeTimeString = (timeString: string): string => {
  if (!timeString) return '';
  // Handle server format with seconds (HH:MM:SS)
  const parts = timeString.split(':');
  if (parts.length >= 2) {
    const result = `${parts[0]}:${parts[1]}`;
    return result;
  }
  return timeString;
};

// Process lock desk hours to normalize time formats
const processLockDeskHoursForUIRendering = (hours: any): any => {
  if (!hours) return {};

  const processed = { ...hours };
  Object.keys(processed).forEach((day) => {
    if (processed[day]) {
      if (processed[day].startTime) {
        processed[day].startTime = normalizeTimeString(processed[day].startTime);
      }
      if (processed[day].endTime) {
        processed[day].endTime = normalizeTimeString(processed[day].endTime);
      }
    }
  });
  return processed;
};

export const EditTenant = ({ onCancel, onSave, tenant }: EditTenantProps): JSX.Element => {
  // Determine if we're in edit mode
  const isEditMode = !!tenant;

  // Form validation schema
  const validationSchema = yup
    .object()
    .shape({
      name: entityNameField('Name').required('Name is required'),
      clientId: identifierField('Client ID'),
      tenantId: identifierField('Tenant ID').optional(),
      urls: yup.array().optional(),
      appearanceSettings: yup.object().optional(),
      priceSettings: yup.object().shape({
        lockDeskSettings: yup.object().shape({
          lockDeskEmailAddress: yup.object().shape({
            address: emailField('Lock desk email').optional(),
          }),
        }),
      }),
      timezone: yup.string().required('Timezone is required'),
    })
    .required();

  // Form default values
  const getFormDefaults = () => {
    return {
      name: tenant?.name || '',
      clientId: tenant?.clientId || '',
      tenantId: tenant?.tenantId || undefined,
      urls: tenant?.urls || [],
      appearanceSettings: tenant?.appearanceSettings || {
        logoUrl: '',
        favIconUrl: '',
        headline: '',
        customCss: '',
      },
      priceSettings: tenant?.priceSettings || {
        isActive: false,
        lenderId: null,
        enableAnonymousPricing: false,
        lockDeskSettings: {
          forceClosed: tenant?.priceSettings?.lockDeskSettings?.forceClosed || false,
          lockDeskEmailAddress: tenant?.priceSettings?.lockDeskSettings?.lockDeskEmailAddress || {
            address: '',
            alias: '',
          },
          lockDeskHours: processLockDeskHoursForUIRendering(tenant?.priceSettings?.lockDeskSettings?.lockDeskHours) || {},
          holidayLockDeskHours: tenant?.priceSettings?.lockDeskSettings?.holidayLockDeskHours || {},
        },
      },
      timezone: tenant?.timezone || '',
    };
  };

  // Initialize form with validation schema and default values
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<Tenant>({
    resolver: yupResolver<any>(validationSchema),
    defaultValues: getFormDefaults(),
    mode: 'onSubmit',
  });

  // Toast ref for enabled products notifications
  const enabledProductsToast = useRef<any>(null);

  // State for enabled products (managed separately via dedicated API)
  const [enabledProducts, setEnabledProducts] = useState<EnabledProducts>(tenant?.enabledProducts || {
    credit: false,
    vox: false,
    fees: false,
    pricing: false,
    automatedUnderwrite: false,
  });
  const [isSavingProducts, setIsSavingProducts] = useState(false);

  // State for URL management
  const [urls, setUrls] = useState<string[]>(tenant?.urls || []);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [newUrl, setNewUrl] = useState<string>('');
  const [editingUrl, setEditingUrl] = useState<string>('');
  const [urlError, setUrlError] = useState<string>('');
  const [urlEditingIndex, setUrlEditingIndex] = useState<number | null>(null);

  // Lock desk hours validation state
  const [lockDeskHoursValidation, setLockDeskHoursValidation] = useState<Record<string, string>>({});

  // Holiday Lock Desk Hours state
  const [newHolidayDate, setNewHolidayDate] = useState<Date | null>(null);
  const [newHolidayStartTime, setNewHolidayStartTime] = useState<Date | null>(null);
  const [newHolidayEndTime, setNewHolidayEndTime] = useState<Date | null>(null);
  const [holidayHours, setHolidayHours] = useState<Record<string, { startTime: string; endTime: string }>>({});

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    if (tenant?.urls) {
      setUrls(tenant.urls);
    }

    if (tenant?.priceSettings?.lockDeskSettings?.holidayLockDeskHours) {
      setHolidayHours(tenant.priceSettings.lockDeskSettings.holidayLockDeskHours);
    }

    // Directly normalize lock desk hours if they exist
    if (tenant?.priceSettings?.lockDeskSettings?.lockDeskHours) {
      const lockDeskHours = tenant.priceSettings.lockDeskSettings.lockDeskHours;

      // Explicitly normalize and update form values for each day
      days.forEach((day) => {
        if (lockDeskHours[day]) {
          if (lockDeskHours[day].startTime) {
            const normalizedStartTime = normalizeTimeString(lockDeskHours[day].startTime);
            setValue(`priceSettings.lockDeskSettings.lockDeskHours.${day}.startTime`, normalizedStartTime);
          }
          if (lockDeskHours[day].endTime) {
            const normalizedEndTime = normalizeTimeString(lockDeskHours[day].endTime);
            setValue(`priceSettings.lockDeskSettings.lockDeskHours.${day}.endTime`, normalizedEndTime);
          }
        }
      });
    }
  }, [tenant, getValues, setValue]);

  // URL management functions
  const addUrl = () => {
    if (newUrl.trim()) {
      setUrls([...urls, newUrl]);
      setNewUrl('');
      setUrlError(''); // Clear URL error when a URL is added
    }
  };

  const editUrl = (index: number) => {
    setUrlEditingIndex(index);
    setEditingUrl(urls[index]);
  };

  const saveEditUrl = () => {
    if (urlEditingIndex !== null && editingUrl.trim()) {
      const newUrls = [...urls];
      newUrls[urlEditingIndex] = editingUrl.trim();
      setUrls(newUrls);
      cancelEditUrl();
    }
  };

  const cancelEditUrl = () => {
    setUrlEditingIndex(null);
    setEditingUrl('');
  };

  const removeUrl = (index: number) => {
    const newUrls = [...urls];
    newUrls.splice(index, 1);
    setUrls(newUrls);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addUrl();
      e.preventDefault();
    }
  };

  // Holiday Lock Desk Hours management functions
  const formatDateKey = (date: Date): string => {
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  };

  // Format time to a consistent string format for comparison and display
  const formatTimeValue = (date: Date): string => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Normalize time string from server to remove seconds (HH:MM:SS → HH:MM)
  const normalizeTimeString = (timeString: string): string => {
    if (!timeString) return '';
    // Handle server format with seconds (HH:MM:SS)
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      const result = `${parts[0]}:${parts[1]}`;
      return result;
    }
    return timeString;
  };

  // Ensure time is in HH:MM:SS format for API consumption
  const ensureTimeFormatBeforeSave = (timeString: string): string => {
    if (!timeString) return '';

    // Count the number of colons to determine format
    const colonCount = (timeString.match(/:/g) || []).length;

    if (colonCount === 0) {
      // No time separators, invalid format
      return timeString;
    } else if (colonCount === 1) {
      // HH:MM format, add seconds
      return `${timeString}:00`;
    } else {
      // Already has seconds or invalid format with too many colons
      // Normalize to ensure proper format
      const parts = timeString.split(':');
      if (parts.length >= 3) {
        return `${parts[0]}:${parts[1]}:${parts[2]}`;
      }
      return timeString;
    }
  };

  // Format time from 24-hour to 12-hour AM/PM format
  const formatTimeTo12Hour = (timeString: string): string => {
    if (!timeString) return '';

    // First normalize to make sure we're working with HH:MM format
    const normalizedTime = normalizeTimeString(timeString);

    // Parse the time string
    const [hoursStr, minutesStr] = normalizedTime.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr;
    const ampm = hours >= 12 ? 'PM' : 'AM';

    // Convert 24-hour to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert '0' to '12'

    return `${hours}:${minutes} ${ampm}`;
  };

  // Check if holiday hours inputs are valid (all fields filled and start time <= end time)
  const isHolidayHoursValid = (): boolean => {
    // All fields must be filled
    if (!newHolidayDate || !newHolidayStartTime || !newHolidayEndTime) {
      return false;
    }

    // Start time must not be later than end time
    const startTime = new Date(`2000-01-01 ${formatTimeValue(newHolidayStartTime)}`);
    const endTime = new Date(`2000-01-01 ${formatTimeValue(newHolidayEndTime)}`);

    return startTime <= endTime;
  };

  const addHolidayHours = () => {
    // Only proceed if all fields are filled and validation passes
    if (isHolidayHoursValid()) {
      const dateKey = formatDateKey(newHolidayDate!);
      const startTime = formatTimeValue(newHolidayStartTime!);
      const endTime = formatTimeValue(newHolidayEndTime!);

      setHolidayHours({
        ...holidayHours,
        [dateKey]: { startTime, endTime },
      });

      // Reset inputs
      setNewHolidayDate(null);
      setNewHolidayStartTime(null);
      setNewHolidayEndTime(null);
    }
  };

  const removeHolidayHours = (dateKey: string) => {
    const updatedHolidayHours = { ...holidayHours };
    delete updatedHolidayHours[dateKey];
    setHolidayHours(updatedHolidayHours);
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEditUrl();
      e.preventDefault();
    }
  };

  const configureLockDeskHoursBeforeSave = (tenant: Tenant): LockDeskHours => {
    const lockDeskHours = { ...tenant.priceSettings.lockDeskSettings.lockDeskHours };

    days.forEach((day) => {
      const dayHours = lockDeskHours[day];
      if (dayHours) {
        if (!dayHours.startTime && !dayHours.endTime) {
          // Remove the key completely for the day when both times are unset
          delete lockDeskHours[day];
        } else if (dayHours.startTime && dayHours.endTime) {
          // Format time values as "HH:mm:ss" for C# TimeOnly
          // Add seconds component which TimeOnly expects
          lockDeskHours[day] = {
            startTime: dayHours.startTime + ':00',
            endTime: dayHours.endTime + ':00',
          };
        }
      }
    });

    return lockDeskHours;
  };

  const configureHolidayLockDeskHoursBeforeSave = (holidayHours: Record<string, { startTime: string; endTime: string }>) => {
    const formattedHolidayHours = { ...holidayHours };

    Object.keys(formattedHolidayHours).forEach((dateKey) => {
      const hours = formattedHolidayHours[dateKey];
      // Format time values as "HH:mm:ss" for C# TimeOnly using the dedicated function
      formattedHolidayHours[dateKey] = {
        startTime: ensureTimeFormatBeforeSave(hours.startTime),
        endTime: ensureTimeFormatBeforeSave(hours.endTime),
      };
    });

    return formattedHolidayHours;
  };

  const onSubmit = async (tenant: Tenant) => {
    // Validate URLs
    if (urls.length === 0) {
      setUrlError('At least one URL is required');
      return;
    } else {
      setUrlError('');
    }

    setIsSubmitting(true);

    try {
      // Process lock desk hours to format time values correctly for C# TimeOnly and remove unset days
      if (tenant.priceSettings?.lockDeskSettings?.lockDeskHours) {
        const lockDeskHours = configureLockDeskHoursBeforeSave(tenant);
        tenant.priceSettings.lockDeskSettings.lockDeskHours = lockDeskHours;
      }

      // Format holiday hours for C# TimeOnly
      // We use a local variable for the formatted hours to avoid manipulating state directly
      let formattedHolidayHours = holidayHours;
      if (Object.keys(holidayHours).length > 0) {
        formattedHolidayHours = configureHolidayLockDeskHoursBeforeSave(holidayHours);
      }

      // Create or update Tenant object when form is valid
      if (onSave) {
        let tenantData: Tenant;

        if (isEditMode && tenant) {
          // Update existing tenant with holidayHours
          const updatedPriceSettings = {
            ...tenant.priceSettings,
            lockDeskSettings: {
              ...tenant.priceSettings.lockDeskSettings,
              holidayLockDeskHours: formattedHolidayHours,
            },
          };

          tenantData = {
            ...tenant,
            name: tenant.name,
            clientId: tenant.clientId,
            urls: urls,
            timezone: tenant.timezone,
            priceSettings: updatedPriceSettings,
          };
        } else {
          // Create new tenant with defaults and holidayHours
          tenantData = {
            name: tenant.name,
            clientId: tenant.clientId,
            tenantId: undefined, // Will be assigned by the backend
            urls: urls,
            appearanceSettings: {
              logoUrl: '',
              favIconUrl: '',
              headline: '',
              customCss: '',
            },
            priceSettings: {
              isActive: false,
              lenderId: null,
              enableAnonymousPricing: false,
              lockDeskSettings: {
                forceClosed: false,
                lockDeskEmailAddress: {
                  address: '',
                  alias: '',
                },
                lockDeskHours: {},
                holidayLockDeskHours: formattedHolidayHours,
              },
            },
            timezone: tenant.timezone || 'UTC',
            enabledProducts: {
              credit: false,
              vox: false,
              fees: false,
              pricing: false,
              automatedUnderwrite: false,
            },
          };
        }

        // Wait for onSave to complete
        await onSave(tenantData);
      }
    } catch (error) {
      console.error('Error saving tenant:', error);
    } finally {
      // Reset loading state when complete
      setIsSubmitting(false);
    }
  };

  return (
    <div className="surface-section p-4 surface-border border-round mb-3 w-full relative">
      <BlockUI blocked={isSubmitting} fullScreen={false}>
        {isSubmitting && (
          <div className="flex align-items-center justify-content-center" style={{ height: '100%' }}>
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled grid">
          <div className="col-12 field mb-0 p-0">
            <FormInputText
              control={control}
              errors={errors}
              field="name"
              label="Name"
              placeholder="Enter Tenant Name"
              required={true}
            />
          </div>

          <div className="col-12 field mb-3 p-0">
            <label className="block mb-1 font-medium">
              URLs<span className="text-red-500 ml-1">*</span>
            </label>
            <div className={`p-2 border-1 ${urlError ? 'p-invalid border-red-500' : 'surface-border'} border-round`}>
              <div className="flex align-items-center">
                <InputText
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Enter new URL"
                  className="flex-grow-1 mr-2 p-inputtext"
                />
                <Button
                  icon="pi pi-plus"
                  className="p-button-sm p-button-outlined"
                  onClick={addUrl}
                  disabled={!newUrl.trim()}
                  tooltip="Add URL"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>

              {urls.length === 0 && <div className="text-color-secondary my-2 text-center text-sm">No URLs added yet</div>}
              {urlError && <small className="p-error block mt-1">{urlError}</small>}

              {urls.map((url, index) => (
                <div
                  key={index}
                  className="mb-1 p-1 flex align-items-center justify-content-between surface-ground border-round"
                >
                  {urlEditingIndex === index ? (
                    <div className="flex align-items-center w-full">
                      <InputText
                        value={editingUrl}
                        onChange={(e) => setEditingUrl(e.target.value)}
                        onKeyDown={handleEditKeyPress}
                        autoFocus
                        className="w-full mr-2 p-inputtext"
                      />
                      <Button
                        icon="pi pi-check"
                        className="p-button-sm p-button-text p-button-success mr-1"
                        onClick={saveEditUrl}
                        tooltip="Save"
                        tooltipOptions={{ position: 'top' }}
                      />
                      <Button
                        icon="pi pi-times"
                        className="p-button-sm p-button-text p-button-danger"
                        onClick={cancelEditUrl}
                        tooltip="Cancel"
                        tooltipOptions={{ position: 'top' }}
                      />
                    </div>
                  ) : (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        editUrl(index);
                      }}
                      className="text-primary hover:underline pl-2"
                    >
                      {url}
                    </a>
                  )}

                  {urlEditingIndex !== index && (
                    <div>
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-sm p-button-text p-button-plain mr-1"
                        onClick={() => editUrl(index)}
                        tooltip="Edit"
                        tooltipOptions={{ position: 'top' }}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-sm p-button-text p-button-danger"
                        onClick={() => removeUrl(index)}
                        tooltip="Delete"
                        tooltipOptions={{ position: 'top' }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="col-12 field mb-1 p-0">
            <FormDropdown
              control={control}
              errors={errors}
              field="timezone"
              label="Timezone"
              options={TimezoneOptions}
              placeholder="Select Timezone"
              showClear={true}
              required={true}
            />
          </div>

          <div className="col-12 field mb-0 p-0">
            <label className="block mb-1 font-medium">Pricing Settings</label>
            <div className="p-4 border-1 surface-border border-round">
              <div className="field-checkbox mb-2">
                <FormCheckbox control={control} errors={errors} field="priceSettings.isActive" label="Active" />
              </div>
              <div className="field-checkbox mb-2">
                <FormCheckbox
                  control={control}
                  errors={errors}
                  field="priceSettings.enableAnonymousPricing"
                  label="Enable Anonymous Pricing"
                />
              </div>

              <div className="mt-2 pt-2">
                <label className="block mb-1 font-medium">Lock Desk Settings</label>
                <div className="p-4 border-1 surface-border border-top-1">
                  <div className="field-checkbox mb-2">
                    <FormCheckbox
                      control={control}
                      errors={errors}
                      field="priceSettings.lockDeskSettings.forceClosed"
                      label="Force Closed"
                    />
                  </div>

                  <div className="field mb-4">
                    <div className="grid">
                      <div className="col-12 md:col-6 mb-2">
                        <FormInputText
                          control={control}
                          errors={errors}
                          field="priceSettings.lockDeskSettings.lockDeskEmailAddress.address"
                          label="Email Address"
                          placeholder="Enter email address"
                        />
                      </div>
                      <div className="col-12 md:col-6 mb-2">
                        <FormInputText
                          control={control}
                          errors={errors}
                          field="priceSettings.lockDeskSettings.lockDeskEmailAddress.alias"
                          label="Email Alias"
                          placeholder="Enter email alias"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block mb-1">Lock Desk Hours</label>
                    <hr className="mt-0 mb-2 border-1 surface-border" style={{ backgroundColor: 'var(--surface-border)' }} />
                    <div className="overflow-x-auto">
                      <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '280px' }}>
                        <tbody>
                          {days.map((day) => (
                            <tr key={day} style={{ height: '32px' }}>
                              <td className="font-medium" style={{ width: '90px', paddingRight: '8px' }}>
                                {day}
                              </td>
                              <td>
                                <div className="flex align-items-center flex-wrap md:flex-nowrap gap-1">
                                  <Controller
                                    name={`priceSettings.lockDeskSettings.lockDeskHours.${day}.startTime`}
                                    control={control}
                                    render={({ field, fieldState }) => {
                                      // Access paired field in a way that doesn't violate React Hooks rules
                                      const endTimeValue = getValues(
                                        `priceSettings.lockDeskSettings.lockDeskHours.${day}.endTime`
                                      );

                                      return (
                                        <Calendar
                                          id={field.name}
                                          value={field.value ? new Date(`2000-01-01T${field.value}`) : null}
                                          onChange={(e) => {
                                            const date = e.value as Date;
                                            const endTime = endTimeValue ? new Date(`2000-01-01T${endTimeValue}`) : null;

                                            if (date) {
                                              // Format time as HH:mm for internal use
                                              const timeString = date.toLocaleTimeString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false,
                                              });
                                              field.onChange(timeString);

                                              // Validate when start time is set but end time is not
                                              if (!endTime) {
                                                setLockDeskHoursValidation((prev) => ({
                                                  ...prev,
                                                  [day]: 'Both start and end times must be set',
                                                }));
                                              } else {
                                                // Check if start time is later than end time
                                                if (date > endTime) {
                                                  setLockDeskHoursValidation((prev) => ({
                                                    ...prev,
                                                    [day]: 'Start time cannot be later than end time',
                                                  }));
                                                } else {
                                                  // Clear validation error
                                                  setLockDeskHoursValidation((prev) => {
                                                    const newState = { ...prev };
                                                    delete newState[day];
                                                    return newState;
                                                  });
                                                }
                                              }
                                            } else {
                                              field.onChange(null);

                                              // Clear validation error if both are unset
                                              if (!endTimeValue) {
                                                setLockDeskHoursValidation((prev) => {
                                                  const newState = { ...prev };
                                                  delete newState[day];
                                                  return newState;
                                                });
                                              } else {
                                                // Show error when end time is set but start time is not
                                                setLockDeskHoursValidation((prev) => ({
                                                  ...prev,
                                                  [day]: 'Both start and end times must be set',
                                                }));
                                              }
                                            }
                                          }}
                                          timeOnly
                                          hourFormat="12"
                                          placeholder="Start Time"
                                          className={classNames({
                                            'p-invalid': fieldState.error || lockDeskHoursValidation[day],
                                          })}
                                          style={{ width: '120px' }}
                                        />
                                      );
                                    }}
                                  />
                                  <span
                                    style={{ margin: '0 4px', display: 'inline-block', width: '20px', textAlign: 'center' }}
                                  >
                                    to
                                  </span>
                                  <Controller
                                    name={`priceSettings.lockDeskSettings.lockDeskHours.${day}.endTime`}
                                    control={control}
                                    render={({ field, fieldState }) => {
                                      // Access paired field in a way that doesn't violate React Hooks rules
                                      const startTimeValue = getValues(
                                        `priceSettings.lockDeskSettings.lockDeskHours.${day}.startTime`
                                      );

                                      return (
                                        <Calendar
                                          id={field.name}
                                          value={
                                            field.value ? new Date(`2000-01-01T${normalizeTimeString(field.value)}`) : null
                                          }
                                          onChange={(e) => {
                                            const date = e.value as Date;
                                            const startTime = startTimeValue
                                              ? new Date(`2000-01-01T${startTimeValue}`)
                                              : null;

                                            if (date) {
                                              // Format time as HH:mm for internal use
                                              const timeString = date.toLocaleTimeString('en-US', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false,
                                              });
                                              field.onChange(timeString);

                                              // Validate when end time is set but start time is not
                                              if (!startTime) {
                                                setLockDeskHoursValidation((prev) => ({
                                                  ...prev,
                                                  [day]: 'Both start and end times must be set',
                                                }));
                                              } else {
                                                // Check if start time is later than end time
                                                if (startTime > date) {
                                                  setLockDeskHoursValidation((prev) => ({
                                                    ...prev,
                                                    [day]: 'Start time cannot be later than end time',
                                                  }));
                                                } else {
                                                  // Clear validation error
                                                  setLockDeskHoursValidation((prev) => {
                                                    const newState = { ...prev };
                                                    delete newState[day];
                                                    return newState;
                                                  });
                                                }
                                              }
                                            } else {
                                              field.onChange(null);

                                              // Clear validation error if both are unset
                                              if (!startTimeValue) {
                                                setLockDeskHoursValidation((prev) => {
                                                  const newState = { ...prev };
                                                  delete newState[day];
                                                  return newState;
                                                });
                                              } else {
                                                // Show error when start time is set but end time is not
                                                setLockDeskHoursValidation((prev) => ({
                                                  ...prev,
                                                  [day]: 'Both start and end times must be set',
                                                }));
                                              }
                                            }
                                          }}
                                          timeOnly
                                          hourFormat="12"
                                          placeholder="End Time"
                                          className={classNames({
                                            'p-invalid': fieldState.error || lockDeskHoursValidation[day],
                                          })}
                                          style={{ width: '120px' }}
                                        />
                                      );
                                    }}
                                  />
                                </div>
                                {lockDeskHoursValidation[day] && (
                                  <small className="p-error block mt-1">{lockDeskHoursValidation[day]}</small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block mb-1">Holiday Lock Desk Hours</label>
                    <hr className="mt-0 mb-2 border-1 surface-border" style={{ backgroundColor: 'var(--surface-border)' }} />
                    <div className="overflow-x-auto w-full">
                      <div className="p-2 border-1 surface-border border-round mb-2 w-full">
                        <div
                          style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                            <Calendar
                              value={newHolidayDate}
                              onChange={(e) => setNewHolidayDate(e.value as Date)}
                              showIcon
                              dateFormat="yy-mm-dd"
                              style={{ width: '150px' }}
                              placeholder="Select Date"
                              className="mr-1"
                            />
                            <Calendar
                              value={newHolidayStartTime}
                              onChange={(e) => setNewHolidayStartTime(e.value as Date)}
                              timeOnly
                              hourFormat="12"
                              style={{ width: '120px' }}
                              placeholder="Start Time"
                            />
                            <span style={{ margin: '0 4px', display: 'inline-block', width: '20px', textAlign: 'center' }}>
                              to
                            </span>
                            <Calendar
                              value={newHolidayEndTime}
                              onChange={(e) => setNewHolidayEndTime(e.value as Date)}
                              timeOnly
                              hourFormat="12"
                              placeholder="End Time"
                              style={{ width: '120px' }}
                            />
                          </div>
                          <Button
                            icon="pi pi-plus"
                            className={
                              isHolidayHoursValid()
                                ? 'p-button-sm p-button-outlined'
                                : 'p-button-sm p-button-outlined p-button-secondary opacity-60'
                            }
                            onClick={addHolidayHours}
                            disabled={!isHolidayHoursValid()}
                            tooltip="Add Holiday Hours"
                            tooltipOptions={{ position: 'top' }}
                          />
                        </div>
                      </div>
                      {Object.keys(holidayHours).length === 0 && (
                        <div className="text-color-secondary my-2 text-center text-sm">No holiday hours added yet</div>
                      )}

                      {Object.keys(holidayHours).length > 0 && (
                        <div className="mt-3">
                          <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '280px' }}>
                            <thead>
                              <tr>
                                <th className="text-left pt-1 pb-2 font-medium">Holiday Date</th>
                                <th className="text-right pt-1 pb-2">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(holidayHours).map(([date, hours]) => (
                                <tr key={date} className="border-bottom-1 surface-border">
                                  <td className="py-2">
                                    <span className="font-medium">{new Date(date).toLocaleDateString()}</span>{' '}
                                    <span className="text-color-secondary">
                                      ({formatTimeTo12Hour(hours.startTime)} to {formatTimeTo12Hour(hours.endTime)})
                                    </span>
                                  </td>
                                  <td className="text-right py-2">
                                    <Button
                                      icon="pi pi-trash"
                                      className="p-button-text p-button-sm p-button-danger"
                                      onClick={() => removeHolidayHours(date)}
                                      tooltip="Delete"
                                      tooltipOptions={{ position: 'top' }}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isEditMode && tenant?.tenantId && (
            <div className="col-12 field mb-1 p-0 mt-3">
              <Toast ref={enabledProductsToast} />
              <label className="block mb-1 font-medium">Enabled Products</label>
              <div className="p-4 border-1 surface-border border-round">
                <div className="grid">
                  {[
                    { key: 'credit' as const, label: 'Credit' },
                    { key: 'vox' as const, label: 'VoX' },
                    { key: 'fees' as const, label: 'Fees' },
                    { key: 'pricing' as const, label: 'Pricing' },
                    { key: 'automatedUnderwrite' as const, label: 'Automated Underwriting' },
                  ].map(({ key, label }) => (
                    <div key={key} className="col-12 md:col-6 field-checkbox mb-2">
                      <input
                        type="checkbox"
                        id={`enabledProducts-${key}`}
                        checked={enabledProducts[key]}
                        onChange={(e) => setEnabledProducts({ ...enabledProducts, [key]: e.target.checked })}
                        className="mr-2"
                      />
                      <label htmlFor={`enabledProducts-${key}`}>{label}</label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-content-end mt-2">
                  <Button
                    type="button"
                    label="Save Products"
                    icon={isSavingProducts ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
                    className="p-button-sm"
                    disabled={isSavingProducts}
                    onClick={async () => {
                      setIsSavingProducts(true);
                      try {
                        const tenantService = TenantService.getInstance();
                        await tenantService.updateEnabledProducts(tenant.tenantId!, enabledProducts);
                        enabledProductsToast.current?.show({ severity: 'success', summary: 'Success', detail: 'Enabled products updated', life: 3000 });
                      } catch (error: any) {
                        enabledProductsToast.current?.show({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to update enabled products', life: 5000 });
                      } finally {
                        setIsSavingProducts(false);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="col-12 field mb-1 p-0 mt-3">
            <label className="block mb-1 font-medium">Appearance Settings</label>
            <div className="p-4 border-1 surface-border border-round">
              <div className="grid">
                <div className="col-12 md:col-6">
                  <FormInputText
                    control={control}
                    errors={errors}
                    field="appearanceSettings.logoUrl"
                    label="Logo URL"
                    placeholder="Enter logo URL"
                    className=""
                  />
                </div>
                <div className="col-12 md:col-6">
                  <FormInputText
                    control={control}
                    errors={errors}
                    field="appearanceSettings.favIconUrl"
                    label="Favicon URL"
                    placeholder="Enter favicon URL"
                    className=""
                  />
                </div>
                <div className="col-12 mb-2">
                  <FormInputText
                    control={control}
                    errors={errors}
                    field="appearanceSettings.headline"
                    label="Headline"
                    placeholder="Enter headline text"
                    className=""
                  />
                </div>
                <div className="col-12 mt-3">
                  <label className="block mb-2 font-medium">Custom CSS</label>
                  <Editor
                    height="300px"
                    theme="vs-dark"
                    defaultLanguage="css"
                    defaultValue={watch('appearanceSettings.customCss') || '/* Add your custom CSS here */'}
                    onChange={(value) => setValue('appearanceSettings.customCss', value || '')}
                    options={{
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      tabSize: 2,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 flex flex-column sm:flex-row justify-content-end gap-2 pt-4">
            <Button
              type="button"
              label="Cancel"
              className="p-button-outlined w-full sm:w-auto"
              style={{ minWidth: 'auto' }}
              onClick={onCancel}
            />
            <Button
              type="submit"
              label={isEditMode ? 'Update' : 'Save'}
              className="p-button w-full sm:w-auto"
              style={{ minWidth: 'auto' }}
            />
          </div>
        </form>
      </BlockUI>
    </div>
  );
};
